/**
 * ورودیِ عددی با محاسبهٔ درجا — مثلِ اکسل.
 *
 * چرا: در ثبتِ کارکرد، یک دوزنده ممکن است چند بار کار آورده باشد (۱۲۰، ۸۰، ۴۵).
 * قبلاً یا باید چند ردیفِ جدا ثبت می‌شد یا کاربر بیرون ماشین‌حساب می‌زد.
 * حالا `۱۲۰+۸۰+۴۵` تایپ می‌شود و با Enter (یا خروج از فیلد) می‌شود ۲۴۵.
 *
 * پشتیبانی: + − × ÷ و پرانتز. ارقامِ فارسی/عربی هم قبول است (نرمال‌سازِ سراسری
 * `digitNormalizer` آن‌ها را ASCII می‌کند، ولی اینجا هم مستقل تبدیل می‌شوند تا
 * کامپوننت به آن وابسته نباشد).
 *
 * محاسبه با پارسرِ خودمان انجام می‌شود، نه eval/Function — ورودیِ کاربر هیچ‌وقت
 * به‌عنوان کد اجرا نمی‌شود.
 */
import React, { useEffect, useRef, useState } from "react";
import { Input, Tooltip } from "antd";
import { evalExpression, isExpression } from "../utils/formula";

export default function FormulaNumber({
  value, onChange, min = 0, max, size, style, placeholder, disabled, status,
}) {
  const [text, setText] = useState(value == null ? "" : String(value));
  const [focused, setFocused] = useState(false);
  const lastCommitted = useRef(value);

  // مقدارِ بیرونی که عوض شد (مثلاً ریست بعد از ثبت) و کاربر داخلِ فیلد نیست
  useEffect(() => {
    if (!focused && value !== lastCommitted.current) {
      setText(value == null ? "" : String(value));
      lastCommitted.current = value;
    }
  }, [value, focused]);

  const clamp = (n) => {
    let v = n;
    if (min != null && v < min) v = min;
    if (max != null && v > max) v = max;
    return v;
  };

  const commit = () => {
    const t = text.trim();
    if (!t) {
      lastCommitted.current = null;
      onChange?.(null);
      return null;
    }
    const n = evalExpression(t);
    if (n === null) return null;              // نامعتبر: متنِ کاربر دست‌نخورده می‌ماند
    const v = clamp(Math.round(n * 100) / 100);
    setText(String(v));
    lastCommitted.current = v;
    onChange?.(v);
    return v;
  };

  const preview = focused && isExpression(text) ? evalExpression(text) : null;
  const invalid = focused && text.trim() && evalExpression(text) === null;

  return (
    <Tooltip open={preview != null} title={`= ${preview}`} placement="top">
      <Input
        value={text}
        size={size}
        disabled={disabled}
        placeholder={placeholder}
        status={invalid ? "error" : status}
        style={{ width: "100%", ...style }}
        inputMode="text"          // تا + و ( روی موبایل هم در دسترس باشد
        onFocus={() => setFocused(true)}
        onChange={(e) => {
          const t = e.target.value;
          setText(t);
          // 🐞 v2.118.0: عددِ ساده همان لحظه به والد می‌رسد (فرمول هنوز با Enter/خروج). قبلاً تا Enter نمی‌زدند
          // هیچ‌چیز ثبت نمی‌شد و دکمهٔ «ثبت کنترل اول» خاموش می‌ماند — کلیک روی دکمهٔ خاموش هم گم می‌شد.
          if (!t.trim()) { lastCommitted.current = null; onChange?.(null); return; }
          if (!isExpression(t)) {
            const n = evalExpression(t);
            if (n !== null) { const v = clamp(Math.round(n * 100) / 100); lastCommitted.current = v; onChange?.(v); }
          }
        }}
        onBlur={() => { commit(); setFocused(false); }}
        onPressEnter={(e) => { e.preventDefault(); commit(); }}
      />
    </Tooltip>
  );
}
